import { Announcement } from "@cryptors/api-client";
import { Box, Chip } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { CrudFilter } from "@refinedev/core";
import {
  DeleteButton,
  EditButton,
  List,
  ShowButton,
  useDataGrid,
} from "@refinedev/mui";
import { useMemo } from "react";

import { SearchField } from "@/components/forms/SearchField";
import { getTranslation } from "@/utils/translation";

const buildSearchFilters = (query: string): CrudFilter[] => {
  const value = query.trim();
  if (!value) return [];
  return [
    {
      operator: "or",
      value: [
        {
          field: "titleTranslation.value",
          operator: "containss",
          value,
        },
        {
          field: "messageTranslation.value",
          operator: "containss",
          value,
        },
      ],
    },
  ];
};

const statusOf = (announcement: Announcement) => {
  if (!announcement.enabled) {
    return { label: "Désactivée", color: "default" as const };
  }
  if (
    announcement.endDate &&
    new Date(announcement.endDate).getTime() <= Date.now()
  ) {
    return { label: "Expirée", color: "warning" as const };
  }
  return { label: "Active", color: "success" as const };
};

export const AnnouncementList = () => {
  const { dataGridProps, setFilters } = useDataGrid<Announcement>({
    resource: "announcements",
    pagination: { pageSize: 25 },
    sorters: { initial: [{ field: "createdAt", order: "desc" }] },
  });

  const columns = useMemo<GridColDef<Announcement>[]>(
    () => [
      {
        field: "titleTranslation",
        headerName: "Titre",
        minWidth: 220,
        flex: 1,
        valueGetter: (_value, row) =>
          getTranslation(row.titleTranslation, "fr")?.value ?? "",
      },
      {
        field: "messageTranslation",
        headerName: "Message",
        minWidth: 300,
        flex: 2,
        valueGetter: (_value, row) =>
          getTranslation(row.messageTranslation, "fr")?.value ?? "",
      },
      {
        field: "status",
        headerName: "Statut",
        minWidth: 120,
        sortable: false,
        renderCell: ({ row }) => {
          const status = statusOf(row);
          return (
            <Chip label={status.label} color={status.color} size="small" />
          );
        },
      },
      {
        field: "endDate",
        headerName: "Date de fin",
        minWidth: 180,
        valueFormatter: (value: string | null) =>
          value ? new Date(value).toLocaleString("fr-FR") : "—",
      },
      {
        field: "actions",
        headerName: "Actions",
        sortable: false,
        align: "center",
        headerAlign: "center",
        minWidth: 160,
        renderCell: ({ row }) => (
          <>
            <EditButton hideText recordItemId={row.id} />
            <ShowButton hideText recordItemId={row.id} />
            <DeleteButton hideText recordItemId={row.id} />
          </>
        ),
      },
    ],
    [],
  );

  return (
    <List>
      <Box sx={{ mb: 2 }}>
        <SearchField
          placeholder="Recherche (titre ou message)"
          buildFilters={buildSearchFilters}
          onSearch={setFilters}
        />
      </Box>
      <DataGrid
        {...dataGridProps}
        columns={columns}
        autoHeight
        disableColumnFilter
        disableColumnSelector
        disableDensitySelector
        pageSizeOptions={[10, 25, 50, 100]}
      />
    </List>
  );
};
